import React, { useState, useEffect } from "react"
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Table,
    Badge,
    Spinner,
    Alert,
    Collapse
} from "reactstrap"
import { post, get } from "../../helpers/api_helper"
import { Plus, List, Send, Paperclip, X, LifeBuoy, RefreshCcw } from "lucide-react"
// Was react-toastify's `toast` with no ToastContainer mounted anywhere in
// this page's tree, so these calls silently rendered nothing at all. Switched
// to the app's shared toastr-based helper (dark/pink-branded, bottom-right,
// stacks cleanly) — the same one used everywhere else in the app — which
// also fixes that: toastr manages its own container, no JSX needed.
import { triggerToast } from "../../utils/toast"
import { api} from '../../utils/variables'
import SEO from "../../components/Common/SEO"
import { PAGE_SEO } from "../../config/seoConfig"
import { generateCanonicalUrl } from "../../utils/seoUtils"
const API_BASE_URL = api

const Support = () => {
    const [loading, setLoading] = useState(false)
    const [tickets, setTickets] = useState([])
    const [fetchingTickets, setFetchingTickets] = useState(false)
    const [error, setError] = useState(null)
    const [showCreateForm, setShowCreateForm] = useState(false)

    // Form state
    const [heading, setHeading] = useState("")
    const [description, setDescription] = useState("")
    const [priority, setPriority] = useState("medium")
    const [attachments, setAttachments] = useState([])

    const fetchTickets = async () => {
        setFetchingTickets(true)
        setError(null)
        try {
            const response = await get(`${API_BASE_URL}/tracker/ticket.list`)

            if (response) {
                if (Array.isArray(response)) {
                    setTickets(response)
                } else if (response.results && Array.isArray(response.results)) {
                    setTickets(response.results)
                } else if (response.data && Array.isArray(response.data)) {
                    setTickets(response.data)
                } else if (response.status === "success" && response.data) {
                    setTickets(Array.isArray(response.data) ? response.data : [])
                }
            }
        } catch (err) {
            console.error("Error fetching tickets:", err)
            setError("Failed to fetch tickets. Please try again later.")
        } finally {
            setFetchingTickets(false)
        }
    }

    useEffect(() => {
        fetchTickets()
    }, [])

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files)
        setAttachments([...attachments, ...files])
    }

    const removeAttachment = (index) => {
        const newAttachments = [...attachments]
        newAttachments.splice(index, 1)
        setAttachments(newAttachments)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!heading.trim() || !description.trim()) {
            triggerToast("Please fill in heading and description", "error")
            return
        }

        setLoading(true)
        try {
            const formData = new FormData()
            formData.append("heading", heading)
            formData.append("description", description)
            formData.append("priority", priority)

            attachments.forEach((file) => {
                formData.append("attachments", file)
            })

            const response = await post(`${API_BASE_URL}/tracker/ticket.create`, formData)

            if (response) {
                triggerToast("Ticket created successfully! Refreshing...", "success")

                // Clear form
                setHeading("")
                setDescription("")
                setPriority("medium")
                setAttachments([])
                setShowCreateForm(false)

                // Hard refresh after a delay so they see the toast
                setTimeout(() => {
                    window.location.reload()
                }, 800)
            } else {
                triggerToast(response?.message || "Failed to create ticket", "error")
            }
        } catch (err) {
            console.error("Error creating ticket:", err)
            triggerToast("An error occurred. Please try again.", "error")
        } finally {
            setLoading(false)
        }
    }

    const themeAccent = "#ff2f6d"
    const cardBg = "#21222D"
    const pageBg = "#171821"
    const borderColor = "#353748"
    const inputBg = "#171821"

    const getPriorityBadgeStyle = (p) => {
        switch (p?.toLowerCase()) {
            case "high": return { backgroundColor: "rgba(246, 37, 90, 0.12)", color: "#F6255A" }
            case "medium": return { backgroundColor: "rgba(241, 180, 76, 0.12)", color: "#f1b44c" }
            case "low": return { backgroundColor: "rgba(80, 165, 241, 0.12)", color: "#50a5f1" }
            default: return { backgroundColor: "rgba(255, 255, 255, 0.06)", color: "rgba(255, 255, 255, 0.6)" }
        }
    }

    const getStatusBadgeStyle = (s) => {
        switch (s?.toLowerCase()) {
            case "open": return { backgroundColor: "rgba(255, 255, 255, 0.06)", color: "rgba(255, 255, 255, 0.6)" }
            case "in_progress": return { backgroundColor: "rgba(241, 180, 76, 0.12)", color: "#f1b44c" }
            case "resolved": return { backgroundColor: "rgba(52, 195, 143, 0.12)", color: "#34c38f" }
            default: return { backgroundColor: "rgba(85, 110, 230, 0.12)", color: "#556ee6" }
        }
    }

    return (
        <div className="page-content" style={{ backgroundColor: pageBg, minHeight: "100vh", fontFamily: "Rubik, sans-serif" }}>
            <SEO
                title={PAGE_SEO.support.title}
                description={PAGE_SEO.support.description}
                keywords={PAGE_SEO.support.keywords}
                canonical={generateCanonicalUrl('/support')}
                robots={PAGE_SEO.support.robots}
            />
            <Container fluid>
                {/* Header Section */}
                <Row className="justify-content-center mb-4">
                    <Col lg={10}>
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                            <div>
                                <h1 className="text-white mb-1" style={{ fontWeight: 600, fontSize: "clamp(20px, 2.5vw, 28px)" }}>
                                    Support & Feedback
                                </h1>
                                <p style={{ color: "rgba(255, 255, 255, 0.6)", fontSize: "14px", margin: 0 }}>
                                    Need help or want to suggest a feature? We're here for you.
                                </p>
                            </div>
                            <Button
                                onClick={() => setShowCreateForm(!showCreateForm)}
                                style={{
                                    backgroundColor: themeAccent,
                                    border: "none",
                                    borderRadius: "10px",
                                    padding: "10px 20px",
                                    fontWeight: 600,
                                    transition: "all 0.2s ease",
                                    boxShadow: `0 4px 15px rgba(255, 47, 109, 0.2)`
                                }}
                                className="d-flex align-items-center gap-2"
                            >
                                {showCreateForm ? <><X size={18} /> Cancel</> : <><Plus size={18} /> New Ticket</>}
                            </Button>
                        </div>
                    </Col>
                </Row>

                {/* Create Ticket Form */}
                <Row className="justify-content-center">
                    <Col lg={10}>
                        <Collapse isOpen={showCreateForm}>
                            <Card className="mb-4" style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}`, borderRadius: "12px" }}>
                                <CardBody className="p-4 p-md-5">
                                    <h2 className="text-white mb-4" style={{ fontWeight: 600, fontSize: '18px' }}>Submit Support Request</h2>
                                    <Form onSubmit={handleSubmit}>
                                        <Row>
                                            <Col md={8}>
                                                <FormGroup className="mb-4">
                                                    <Label className="text-light" style={{ fontSize: "13px", fontWeight: 500, opacity: 0.8 }}>Subject</Label>
                                                    <Input
                                                        type="text"
                                                        placeholder="Quick summary of your issue"
                                                        value={heading}
                                                        onChange={(e) => setHeading(e.target.value)}
                                                        style={{
                                                            backgroundColor: inputBg,
                                                            border: "1px solid rgba(255,255,255,0.06)",
                                                            color: "white",
                                                            padding: "12px",
                                                            borderRadius: "8px",
                                                            fontSize: "14px"
                                                        }}
                                                        required
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={4}>
                                                <FormGroup className="mb-4">
                                                    <Label className="text-light" style={{ fontSize: "13px", fontWeight: 500, opacity: 0.8 }}>Priority</Label>
                                                    <Input
                                                        type="select"
                                                        value={priority}
                                                        onChange={(e) => setPriority(e.target.value)}
                                                        style={{
                                                            backgroundColor: inputBg,
                                                            border: "1px solid rgba(255,255,255,0.06)",
                                                            color: "white",
                                                            padding: "12px",
                                                            borderRadius: "8px",
                                                            fontSize: "14px"
                                                        }}
                                                    >
                                                        <option value="low">Low</option>
                                                        <option value="medium">Medium</option>
                                                        <option value="high">High</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        <FormGroup className="mb-4">
                                            <Label className="text-light" style={{ fontSize: "13px", fontWeight: 500, opacity: 0.8 }}>Description</Label>
                                            <Input
                                                type="textarea"
                                                rows="5"
                                                placeholder="Please provide details..."
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                style={{
                                                    backgroundColor: inputBg,
                                                    border: "1px solid rgba(255,255,255,0.06)",
                                                    color: "white",
                                                    padding: "12px",
                                                    borderRadius: "8px",
                                                    fontSize: "14px"
                                                }}
                                                required
                                            />
                                        </FormGroup>

                                        <FormGroup className="mb-4">
                                            <Label className="text-light" style={{ fontSize: "13px", fontWeight: 500, opacity: 0.8 }}>Attachments</Label>
                                            <div className="d-flex flex-wrap gap-2 mb-3">
                                                {attachments.map((file, index) => (
                                                    <div key={index} className="d-flex align-items-center p-2" style={{ backgroundColor: "#1c1d26", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.06)" }}>
                                                        <span className="text-white text-truncate me-2" style={{ maxWidth: "150px", fontSize: "12px" }}>{file.name}</span>
                                                        <X size={14} className="text-danger" style={{ cursor: "pointer" }} onClick={() => removeAttachment(index)} />
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="position-relative" style={{ maxWidth: "180px" }}>
                                                <Input
                                                    type="file"
                                                    multiple
                                                    onChange={handleFileChange}
                                                    className="position-absolute opacity-0"
                                                    style={{ cursor: "pointer", top: 0, left: 0, width: "100%", height: "100%", zIndex: 2 }}
                                                />
                                                <Button outline color="light" className="w-100 d-flex align-items-center justify-content-center gap-2"
                                                    style={{
                                                        borderStyle: "dashed",
                                                        borderColor: "rgba(255,255,255,0.12)",
                                                        borderRadius: "8px",
                                                        fontSize: "13px",
                                                        padding: "8px"
                                                    }}
                                                >
                                                    <Paperclip size={14} /> Attach Files
                                                </Button>
                                            </div>
                                        </FormGroup>

                                        <Button
                                            type="submit"
                                            disabled={loading}
                                            style={{
                                                backgroundColor: themeAccent,
                                                border: "none",
                                                borderRadius: "10px",
                                                padding: "12px 35px",
                                                fontWeight: 600
                                            }}
                                        >
                                            {loading ? <Spinner size="sm" /> : <><Send size={16} className="me-2" /> Submit Ticket</>}
                                        </Button>
                                    </Form>
                                </CardBody>
                            </Card>
                        </Collapse>
                    </Col>
                </Row>

                {/* Tickets History List */}
                <Row className="justify-content-center">
                    <Col lg={10}>
                        <div className="mb-4 d-flex align-items-center justify-content-between">
                            <h2 className="text-white m-0" style={{ fontWeight: 600, fontSize: '18px' }}>Ticket History</h2>
                            <button
                                onClick={fetchTickets}
                                disabled={fetchingTickets}
                                style={{
                                    background: "transparent",
                                    border: "1px solid rgba(255,255,255,0.12)",
                                    color: "#fff",
                                    borderRadius: "10px",
                                    padding: "7px 15px",
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px"
                                }}
                            >
                                <RefreshCcw size={14} className={fetchingTickets ? "spin" : ""} />
                                {fetchingTickets ? "Refreshing..." : "Refresh"}
                            </button>
                        </div>

                        <Card style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}`, borderRadius: "12px", overflow: "hidden" }}>
                            <CardBody className="p-0">
                                {fetchingTickets && tickets.length === 0 ? (
                                    <div className="text-center py-5">
                                        <Spinner size="lg" style={{ color: themeAccent }} />
                                        <p className="text-muted mt-3">Fetching your history...</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <Table className="m-0" style={{ borderCollapse: "separate", borderSpacing: "0 8px", padding: "8px" }}>
                                            <thead>
                                                <tr style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px" }}>
                                                    <th className="text-muted" style={{ border: "none", padding: "15px 20px" }}>Ticket ID</th>
                                                    <th className="text-muted" style={{ border: "none", padding: "15px 20px" }}>Subject</th>
                                                    <th className="text-muted text-center" style={{ border: "none", padding: "15px 20px" }}>Priority</th>
                                                    <th className="text-muted text-center" style={{ border: "none", padding: "15px 20px" }}>Status</th>
                                                    <th className="text-muted text-end" style={{ border: "none", padding: "15px 20px" }}>Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tickets.length > 0 ? (
                                                    tickets.map((ticket, index) => (
                                                        <tr key={ticket.id || index} style={{ backgroundColor: "#1c1d26" }}>
                                                            <td style={{ border: "none", padding: "15px 20px", borderTopLeftRadius: "10px", borderBottomLeftRadius: "10px", color: "rgba(255,255,255,0.5)", fontSize: "13px" }}>
                                                                #{ticket.id}
                                                            </td>
                                                            <td style={{ border: "none", padding: "15px 20px" }}>
                                                                <div className="text-white fw-medium mb-1" style={{ fontSize: "14px" }}>{ticket.heading}</div>
                                                                <div className="text-muted text-truncate" style={{ maxWidth: "300px", fontSize: "12px", opacity: 0.6 }}>
                                                                    {ticket.description}
                                                                </div>
                                                            </td>
                                                            <td className="text-center" style={{ border: "none", padding: "15px 20px" }}>
                                                                <span className="badge px-3 py-2" style={{ ...getPriorityBadgeStyle(ticket.priority), borderRadius: "6px", fontWeight: 600, fontSize: "10px" }}>
                                                                    {ticket.priority?.toUpperCase()}
                                                                </span>
                                                            </td>
                                                            <td className="text-center" style={{ border: "none", padding: "15px 20px" }}>
                                                                <span className="badge px-3 py-2" style={{ ...getStatusBadgeStyle(ticket.status || "open"), borderRadius: "6px", fontWeight: 600, fontSize: "10px" }}>
                                                                    {ticket.status?.toUpperCase() || "OPEN"}
                                                                </span>
                                                            </td>
                                                            <td className="text-end" style={{ border: "none", padding: "15px 20px", borderTopRightRadius: "10px", borderBottomRightRadius: "10px", color: "rgba(255,255,255,0.5)", fontSize: "13px" }}>
                                                                {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : "-"}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" className="text-center py-5">
                                                            <div className="mb-3 opacity-20">
                                                                <LifeBuoy size={48} style={{ color: "rgba(255,255,255,0.5)" }} />
                                                            </div>
                                                            <p style={{ color: "rgba(255,255,255,0.4)" }}>No tickets found. Submit a request if you need help!</p>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                                {error && <div className="p-3"><Alert color="danger" style={{ backgroundColor: "rgba(244, 106, 106, 0.1)", border: "none", color: "#f46a6a", margin: 0 }}>{error}</Alert></div>}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <style>{`
                .spin {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .table tr {
                    margin-bottom: 8px;
                }
                .table-responsive::-webkit-scrollbar {
                    height: 5px;
                }
                .table-responsive::-webkit-scrollbar-track {
                    background: transparent;
                }
                .table-responsive::-webkit-scrollbar-thumb {
                    background: rgba(255,255,255,0.1);
                    border-radius: 10px;
                }
                button:hover {
                    opacity: 0.9;
                }
            `}</style>
        </div>
    )
}

export default Support
